import { Button, Typography } from '@breetometer/components';
import { StarRatingIcon } from 'BreetAssets';
import { handleApiError, mapArrayFromLength } from 'BreetHelpers';
import { useRatingsApi } from 'BreetHooks';
import { closeModals, selectModals, useAddTransactionRatingMutation, useAppDispatch, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';

export const DialogRatings = () => {
	const [addTransactionRating, { isLoading }] = useAddTransactionRatingMutation();

	const dispatch = useAppDispatch();

	const modals = useAppSelector(selectModals);

	const modal = modals.find((modalItem) => modalItem.id === 'ratings');

	const transactionIdObject = modal?.metadata as { id: string } | undefined;

	const ratingsApi = useRatingsApi();

	const [comment, setComment] = useState<string>('');

	const [error, setError] = useState<string>('');

	const onHandleComment = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setComment(e.target.value);

		if (!e.target.value) {
			setError('Comment is missing');
			return;
		}

		setError('');
	}, []);

	const onSubmitRatings = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			if (!ratingsApi.rating) {
				setError('Select rating');
				return;
			}

			if (!comment) {
				setError('Comment is required');
				return;
			}

			if (transactionIdObject?.id)
				addTransactionRating({ id: transactionIdObject.id, comment, rating: ratingsApi.rating })
					.unwrap()
					.then(() => {
						dispatch(closeModals());
					})
					.catch(handleApiError);
		},
		[comment, dispatch, addTransactionRating, ratingsApi.rating, transactionIdObject?.id]
	);

	return (
		<div className='dialogratings'>
			<Typography as='h1'>Rate Your Experience</Typography>

			<form onSubmit={onSubmitRatings}>
				<Button
					variant='native'
					className='dialogratings_ratings'
					onMouseLeave={ratingsApi.onHover(null)}
				>
					{mapArrayFromLength(5).map((i, index) => {
						const isFilled = ratingsApi.checkIsFilled(index);

						return (
							<StarRatingIcon
								key={i}
								variant={isFilled ? 'filled' : 'default'}
								onMouseEnter={ratingsApi.onHover(index)}
								aria-label={`Rate ${index + 1} out of 5`}
								role='button'
								tabIndex={0}
								onClick={ratingsApi.setRating(index)}
								className='cursor_pointer'
							/>
						);
					})}
				</Button>

				<Typography
					as='label'
					htmlFor='transactionratings'
				>
					Leave us a review
				</Typography>

				<textarea
					id='transactionratings'
					placeholder='How do you feel about this transaction?'
					onChange={onHandleComment}
					value={comment}
				/>

				{!!error && (
					<Typography
						as='p'
						className='bt_input-error-message'
					>
						{error}
					</Typography>
				)}

				<Button
					label='Submit'
					type='submit'
					isLoading={isLoading}
					disabled={!!error || !comment || ratingsApi.rating === 0}
				/>
			</form>
		</div>
	);
};
